import { HttpEventType, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { QueryCacheService } from './query-cache.service';
import { of, tap } from 'rxjs';
import { SKIP_CACHE, STALE_TIME } from './cache.tokens';

const normalizeUrlPath = (url: string) => {
  try {
    return new URL(url).pathname;
  } catch {
    return url.split('?')[0];
  }
};

export const cacheInterceptor: HttpInterceptorFn = (req, next) => {
  const queryCacheService = inject(QueryCacheService);
  const skipCache = req.context.get(SKIP_CACHE);

  if (skipCache) {
    return next(req);
  }

  if (req.method === 'GET') {
    const queryKey = queryCacheService.createKeyFromRequest(req);
    const existingResponse = queryCacheService.getCachedResponse(queryKey);

    if (existingResponse) {
      return of(existingResponse);
    }

    return next(req).pipe(
      tap((event) => {
        if (event.type === HttpEventType.Response && event.ok) {
          queryCacheService.setCachedData(queryKey, event.body, req.context.get(STALE_TIME));
        }
      })
    );
  }

  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method)) {
    return next(req).pipe(
      tap((event) => {
        if (event.type === HttpEventType.Response && event.ok) {
          // Invalidate any cached GET requests for this URL and its immediate parent.
          const key = queryCacheService.createKeyFromRequest(req);
          queryCacheService.invalidate(key);

          // Also invalidate the parent collection URL so that list/grid GETs
          // (e.g. /api/resource) are busted when a resource-specific mutation
          // (e.g. PUT /api/resource/{id}) succeeds.
          //
          // Always derive the parent from the URL's path component only, so that
          // absolute CDE URLs (https://api.cde.com/api/resource/123) produce the
          // same parent path as the equivalent local relative URL (/api/resource/123).
          const urlPath = normalizeUrlPath(req.url);
          const parentPath = urlPath.replace(/\/[^/]+$/, '');
          if (parentPath && parentPath !== urlPath) {
            queryCacheService.invalidate(parentPath);
          }
        }
      })
    );
  }

  return next(req);
};
